import type { MechanicDefinition } from "./types";

export const resting: MechanicDefinition = {
  id: "resting",
  disabled: true,
  name: "Short & Long Rests",
  description: "D&D rest mechanics: hit dice, HP recovery, resource restoration, and rest variants",
  icon: "Moon",
  category: "systems",
  auditPasses: 1, // ⭐
  aiHints: {
    purpose: "Provides structured rest mechanics — short rests for partial recovery, long rests for full recovery.",
    whenToUse: "D&D-style games or any world where rest/recovery is a meaningful resource management decision.",
    synergies: ["combat", "survival", "time_passage", "magic_system"],
    conflicts: [],
    prerequisites: [],
    tags: ["rest", "recovery", "short-rest", "long-rest", "hit-dice", "d&d"],
    complexity: 2,
    exampleConfig: { rest_variant: "standard", hit_dice_enabled: true, safe_location_required: true },
    stateVariables: ["hit_dice_current", "hit_dice_max", "last_long_rest_day"],
  },
  configSchema: [
    {
      key: "rest_variant",
      label: "Rest Variant",
      type: "select",
      options: [
        { value: "standard", label: "Standard D&D", description: "Short rest = 1 hour, Long rest = 8 hours" },
        { value: "gritty_realism", label: "Gritty Realism", description: "Short rest = 8 hours, Long rest = 7 days" },
        { value: "epic_heroism", label: "Epic Heroism", description: "Short rest = 5 minutes, Long rest = 1 hour" },
      ],
      default: "standard",
    },
    {
      key: "hit_dice_enabled",
      label: "Hit Dice Healing",
      type: "boolean",
      default: true,
      description: "Spend hit dice during short rests to heal (roll + CON mod per die)",
    },
    {
      key: "safe_location_required",
      label: "Safe Location Required for Long Rest",
      type: "boolean",
      default: true,
      description: "Long rests require a safe location (inn, camp, shelter). Dangerous areas only allow short rests.",
    },
  ],
  systemPromptSection: (config) => {
    const variant = config.rest_variant || "standard";
    const hitDice = config.hit_dice_enabled !== false;
    const safeRequired = config.safe_location_required !== false;

    const durations: Record<string, { short: string; long: string }> = {
      standard: { short: "1 hour", long: "8 hours" },
      gritty_realism: { short: "8 hours", long: "7 days" },
      epic_heroism: { short: "5 minutes", long: "1 hour" },
    };
    const d = durations[variant] || durations.standard;

    return `## RESTING SYSTEM (Hard Mechanic)
- Variant: ${variant}. Short rest = ${d.short}. Long rest = ${d.long}.

### SHORT REST
- Duration: ${d.short}. Can be taken anywhere.
${hitDice ? `- HIT DICE: Spend hit dice to heal. Each die: roll hit die + CON modifier. Maximum of (level) hit dice total.
- Track hit_dice_current and hit_dice_max in state.` : "- No hit dice healing during short rest."}
- Recovers: Some class features (Fighter Action Surge, Warlock spell slots, etc.).
- Does NOT recover: HP (without hit dice), spell slots (except Warlock), long-rest features.

### LONG REST
- Duration: ${d.long}. ${safeRequired ? "Requires a safe location. Cannot long rest in hostile territory." : "Can be taken anywhere."}
- Recovers: ALL HP, up to half total hit dice (rounded down, minimum 1), all spell slots, all daily abilities.
- Reduces exhaustion by 1 level.
- Clears all short-duration conditions.
- Can only benefit from 1 long rest per 24-hour period.
- If interrupted by combat (1+ hour of activity), must restart.

### REST INTERACTIONS
- Advances time via time_passage mechanic if active.
- Satisfies survival rest need if survival mechanic is active.
- Emits rest_completed event for cross-mechanic reactions.
- Use short_rest or long_rest tools to process rests.`;
  },
  initializeState: (config) => ({
    hit_dice_current: 1,
    hit_dice_max: 1,
    last_long_rest_day: 0,
  }),
  onEvent: (event, context) => {
    // When level up, increase hit dice max
    if (event.type === "level_up") {
      const newLevel = event.data.new_level || 1;
      return {
        stateUpdates: {
          hit_dice_max: newLevel,
          hit_dice_current: Math.min((context.state.variables?.hit_dice_current || 0) + 1, newLevel),
        },
        narrativeNotes: [`🎲 Hit dice max increased to ${newLevel}`],
      };
    }
    return null;
  },
  widgets: [
    {
      id: "rest_tracker",
      type: "inline",
      label: "Rest & Recovery",
      icon: "Moon",
      component: "RestWidget",
      dataBindings: {
        hit_dice_current: "variables.hit_dice_current",
        hit_dice_max: "variables.hit_dice_max",
        last_long_rest_day: "variables.last_long_rest_day",
        game_day: "variables.game_day",
      },
      visibility: "when_data",
      priority: 15,
    },
    {
      id: "rest_status",
      type: "status_bar_item",
      label: "Hit Dice",
      icon: "Dice1",
      component: "RestStatus",
      dataBindings: {
        hit_dice_current: "variables.hit_dice_current",
        hit_dice_max: "variables.hit_dice_max",
      },
      visibility: "when_data",
      priority: 14,
    },
  ],
  tools: [
    {
      type: "function",
      function: {
        name: "short_rest",
        description: "Take a short rest. Optionally spend hit dice to heal.",
        parameters: {
          type: "object",
          properties: {
            hit_dice_spent: { type: "number", description: "Number of hit dice to spend healing (each rolls hit die + CON mod)" },
            narrative: { type: "string", description: "What happens during the rest" },
          },
          required: [],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "long_rest",
        description: "Take a long rest. Full HP recovery, spell slots restored, half hit dice recovered, exhaustion reduced by 1.",
        parameters: {
          type: "object",
          properties: {
            location: { type: "string", description: "Where the rest takes place (inn, camp, cave, etc.)" },
            narrative: { type: "string", description: "What happens during the rest" },
            interrupted: { type: "boolean", description: "Whether the rest was interrupted by combat/activity" },
          },
          required: [],
        },
      },
    },
  ],
};
